import { api } from './client';

/**
 * Settings API client for OpenRouter configuration
 */
export const settingsApi = {
  /**
   * Get user settings including API key status and model preferences
   * @returns {Promise<Object>} Settings object with api_key_configured, credit info, and models
   */
  getSettings: () =>
    api.get('/me/settings'),

  /**
   * Set or update OpenRouter API key
   * @param {string} apiKey - The OpenRouter API key
   * @returns {Promise<Object>} Updated settings with validation result
   */
  setApiKey: (apiKey) =>
    api.put('/me/settings/api-key', { api_key: apiKey }),

  /**
   * Delete the stored API key
   * @returns {Promise<void>}
   */
  deleteApiKey: () =>
    api.delete('/me/settings/api-key'),

  /**
   * Update model preferences
   * @param {Object} updates - Model updates (e.g., {chat: "openai/gpt-4"})
   * @returns {Promise<Object>} Updated settings
   */
  updateModels: (updates) =>
    api.patch('/me/settings/models', updates),

  /**
   * Reset all per-function model preferences to defaults
   * @returns {Promise<Object>} Updated settings with default models
   */
  resetModels: () =>
    api.post('/me/settings/models/reset'),

  /**
   * Get the writer's raw model-menu state (added/removed ids against the
   * default set). Rarely needed directly — getAvailable() already returns
   * the computed menu ready for rendering.
   * @returns {Promise<Object>} { data: { added: string[], removed: string[] } }
   */
  getModelMenu: () =>
    api.get('/me/settings/model-menu'),

  /**
   * Check a model into the writer's model menu — a brand-new catalog pick
   * or re-checking a previously-hidden default (the backend figures out
   * which).
   * @param {string} modelId - OpenRouter model ID
   * @returns {Promise<Object>} { data: { models: { default, added } } }
   */
  addToModelMenu: (modelId) =>
    api.post('/me/settings/model-menu/add', { model_id: modelId }),

  /**
   * Uncheck a model from the writer's model menu (default or added — the
   * backend handles both cases).
   * @param {string} modelId - OpenRouter model ID
   * @returns {Promise<Object>} { data: { models: { default, added } } }
   */
  hideFromModelMenu: (modelId) =>
    api.post('/me/settings/model-menu/hide', { model_id: modelId }),

  /**
   * Re-check every default model. Never touches the writer's own additions.
   * @returns {Promise<Object>} { data: { models: { default, added } } }
   */
  restoreDefaultModels: () =>
    api.post('/me/settings/model-menu/restore-defaults'),

  /**
   * Search OpenRouter's live public model catalog (server-side cached ~1h,
   * filtered to text-generation models).
   * @param {string} [search] - Search term; omitted/empty returns the full filtered catalog
   * @returns {Promise<Object>} { data: { models: [{id, name, maker, pricing}] } }
   */
  getOpenRouterCatalog: (search) =>
    api.get(`/me/settings/openrouter-catalog${search ? `?search=${encodeURIComponent(search)}` : ''}`),

  /**
   * Get user preferences (working method, etc.)
   * @returns {Promise<Object>} User preferences
   */
  getPreferences: () =>
    api.get('/users/me/preferences'),

  /**
   * Update user preferences
   * @param {Object} updates - Preference updates (e.g., {working_method: "balanced"})
   * @returns {Promise<Object>} Updated preferences
   */
  updatePreferences: (updates) =>
    api.put('/users/me/preferences', updates)
};

export default settingsApi;
